import { useState, useEffect, useCallback } from 'react';
import { Bell, TrendingUp, Wallet, PieChart, Zap, ArrowRight, Loader2, AlertCircle, ChevronRight } from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR } from '@/utils/currency';
import KYCStatusBadge from './KYCStatusBadge';
import KYCModal from './KYCModal';
import { useMockData as shouldUseMockData } from '@/mocks/config';

// Mock data for Phase 1
const MOCK_PORTFOLIO_STATS = {
  totalCommitted: 5300000,
  totalInvested: 2850000,
  totalInEscrow: 2850000,
  propertyCount: 4,
  actionRequired: 2,
};

export default function InvestorHomeTab({ user, setActiveTab }) {
  const [kycStatus, setKycStatus] = useState(null);
  const [kycLoading, setKycLoading] = useState(true);
  const [showKYCModal, setShowKYCModal] = useState(false);
  const [portfolioStats, setPortfolioStats] = useState({
    totalCommitted: 0,
    totalInvested: 0,
    totalInEscrow: 0,
    propertyCount: 0,
    actionRequired: 0,
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [refetchKey, setRefetchKey] = useState(0);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const refetchKYCStatus = useCallback(() => {
    setRefetchKey(prev => prev + 1);
  }, []);

  useEffect(() => {
    const fetchKYCStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/investor/kyc/status/${user.id}`);
        const data = await response.json();
        setKycStatus(data.status || 'NOT_STARTED');
      } catch (error) {
        setKycStatus('NOT_STARTED');
      } finally {
        setKycLoading(false);
      }
    };

    const fetchPortfolioStats = async () => {
      const isMockMode = shouldUseMockData('investor', 'home');
      
      if (isMockMode) {
        setPortfolioStats(MOCK_PORTFOLIO_STATS);
        setStatsLoading(false);
        return;
      }
      
      try {
        const response = await api.get(`/metrics/investor/${user.id}`);
        const d = response.data;
        
        setPortfolioStats({
          totalCommitted: d.totalCommitted || 0,
          totalInvested: d.totalInvested || 0,
          totalInEscrow: 0,
          propertyCount: d.totalInvestments || 0,
          actionRequired: d.actionRequired || 0,
        });
      } catch (error) {
        console.error('Error fetching portfolio stats:', error);
        setPortfolioStats(MOCK_PORTFOLIO_STATS);
      } finally {
        setStatsLoading(false);
      }
    };

    if (user?.id) {
      fetchKYCStatus();
      fetchPortfolioStats();
    }
  }, [user?.id, API_URL, refetchKey]);

  const hasCompletedKYC = kycStatus === 'VERIFIED';

  const assetClasses = [
    {
      id: 'land',
      title: 'AltSlice · Land',
      description: 'Pre-leased commercial land parcels with long-term appreciation potential',
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1200&q=80',
      enabled: true
    },
    {
      id: 'residences',
      title: 'AltSlice · Residences',
      description: 'Premium residential properties in high-growth micro-markets',
      image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=1200&q=80',
      enabled: true
    },
    {
      id: 'art',
      title: 'AltSlice · Art',
      description: 'Curated fine art and collectibles from established artists',
      image: 'https://images.unsplash.com/photo-1547826039-bfc35e0f1ea8?w=1200&q=80',
      enabled: false,
      comingSoon: 'Coming Soon'
    }
  ];

  const formatStatValue = (val) => {
    if (val >= 10000000) return `₹${(val/10000000).toFixed(2)} Cr`;
    if (val >= 100000) return `₹${(val/100000).toFixed(2)}L`;
    if (val >= 1000) return `₹${(val/1000).toFixed(1)}K`;
    return `₹${val.toLocaleString('en-IN')}`;
  };

  const quickStats = [
    { 
      label: 'Total Committed', 
      value: statsLoading ? '...' : formatStatValue(portfolioStats.totalCommitted), 
      icon: PieChart 
    },
    { 
      label: 'Total Invested', 
      value: statsLoading ? '...' : formatStatValue(portfolioStats.totalInvested), 
      icon: TrendingUp 
    },
    { 
      label: 'In Escrow', 
      value: statsLoading ? '...' : formatStatValue(portfolioStats.totalInEscrow), 
      icon: Wallet 
    },
    { 
      label: 'Properties', 
      value: statsLoading ? '...' : portfolioStats.propertyCount.toString(), 
      icon: Zap 
    }
  ];

  return (
    <div className="p-6 md:p-10">
      {/* Header with KYC Badge */}
      <div className="mb-10 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-light text-white mb-2">
            Welcome back, {user?.name?.split(' ')[0] || 'Investor'}
          </h1>
          <p className="text-gray-500 text-sm">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
        {!kycLoading && (
          <KYCStatusBadge 
            userId={user?.id} 
            onClick={() => setShowKYCModal(true)}
            size="default"
          />
        )}
      </div>

      {/* KYC Banner - Shows if not completed */}
      {!kycLoading && !hasCompletedKYC && kycStatus !== 'SUBMITTED' && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-sm p-5 mb-8 flex items-start gap-4">
          <Bell className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-white font-medium mb-1">Complete your KYC to start investing</p>
            <p className="text-sm text-gray-400 mb-4">Verification is required as per regulatory guidelines</p>
            <button 
              onClick={() => setShowKYCModal(true)}
              className="bg-yellow-500 hover:bg-yellow-600 text-black px-6 py-2 text-sm font-semibold uppercase tracking-wider transition-colors"
              data-testid="complete-kyc-btn"
            >
              Complete KYC Now
            </button>
          </div>
        </div>
      )}

      {/* KYC Pending Banner */}
      {!kycLoading && kycStatus === 'SUBMITTED' && (
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-sm p-5 mb-8 flex items-start gap-4">
          <Bell className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-white font-medium mb-1">KYC Verification Pending</p>
            <p className="text-sm text-gray-400">Your documents are being verified. You'll be able to invest once approved.</p>
          </div>
        </div>
      )}

      {/* Action Required Alert - Links to Portfolio */}
      {!statsLoading && portfolioStats.actionRequired > 0 && (
        <button 
          onClick={() => setActiveTab('portfolio')}
          className="w-full bg-red-500/10 border border-red-500/30 rounded-sm p-4 mb-8 flex items-center justify-between hover:bg-red-500/15 transition-colors group"
          data-testid="action-required-alert"
        >
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <div className="text-left">
              <p className="text-white font-medium">
                {portfolioStats.actionRequired} investment{portfolioStats.actionRequired !== 1 ? 's' : ''} need your attention
              </p>
              <p className="text-sm text-red-400/70">Sign documents or complete payments to proceed</p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-red-400 group-hover:translate-x-1 transition-transform" />
        </button>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 border border-white/10 mb-10" data-testid="portfolio-quick-stats">
        {quickStats.map((stat, idx) => {
          const Icon = stat.icon;
          return (
            <div key={idx} className="bg-black px-4 md:px-6 py-5 md:py-6">
              <div className="flex items-center justify-between mb-2 md:mb-3">
                <p className="text-[10px] text-gray-600 uppercase tracking-widest">{stat.label}</p>
                {statsLoading ? (
                  <Loader2 className="w-4 h-4 text-gray-700 animate-spin" />
                ) : (
                  <Icon className="w-4 h-4 text-gray-700" />
                )}
              </div>
              <p className={`font-mono text-xl md:text-3xl font-light ${statsLoading ? 'text-gray-600' : 'text-white'}`}>
                {stat.value}
              </p>
            </div>
          );
        })}
      </div>

      {/* Asset Classes */}
      <div className="mb-10">
        <p className="text-[10px] text-gray-600 uppercase tracking-widest mb-4">Explore Investment Products</p>
        <div className="grid md:grid-cols-3 gap-4 md:gap-6">
          {assetClasses.map((asset) => (
            <button
              key={asset.id}
              onClick={() => asset.enabled && setActiveTab(asset.id)}
              disabled={!asset.enabled}
              className={`border border-white/10 overflow-hidden text-left group transition-all ${
                asset.enabled ? 'hover:border-[#4ADE80]/50 cursor-pointer' : 'opacity-50 cursor-not-allowed'
              }`}
            >
              <div className="relative h-64 md:h-80">
                <img src={asset.image} alt={asset.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                
                {!asset.enabled && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                    <div className="text-center">
                      <p className="text-white font-medium text-lg mb-1">{asset.comingSoon}</p>
                    </div>
                  </div>
                )}
                
                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                  <h3 className="text-xl md:text-2xl text-white font-medium mb-2">{asset.title}</h3>
                  <p className="text-sm text-gray-300 line-clamp-2">{asset.description}</p>
                  {asset.enabled && (
                    <div className="mt-4 flex items-center gap-2 text-[#4ADE80]">
                      <span className="text-sm uppercase tracking-wider">Explore</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-4">
        <button
          onClick={() => setActiveTab('portfolio')}
          className="border border-white/10 p-4 md:p-6 text-left hover:border-[#4ADE80]/30 hover:bg-[#4ADE80]/5 transition-colors group"
          data-testid="view-portfolio-btn"
        >
          <PieChart className="w-6 h-6 text-[#4ADE80] mb-3" />
          <h3 className="text-white font-medium mb-1">View Portfolio</h3>
          <p className="text-sm text-gray-500">Track investments & pending actions</p>
        </button>
        <button
          onClick={() => setActiveTab('transactions')}
          className="border border-white/10 p-4 md:p-6 text-left hover:border-white/20 transition-colors group"
          data-testid="view-transactions-btn"
        >
          <Wallet className="w-6 h-6 text-gray-500 mb-3" />
          <h3 className="text-white font-medium mb-1">Transactions</h3>
          <p className="text-sm text-gray-500">View payment history</p>
        </button>
      </div>

      {/* KYC Modal */}
      <KYCModal
        isOpen={showKYCModal}
        onClose={() => setShowKYCModal(false)}
        userId={user?.id}
        onComplete={() => {
          setShowKYCModal(false);
          refetchKYCStatus();
        }}
      />
    </div>
  );
}
